import update from 'immutability-helper';
import { uniqueId as lodashUniqueId } from 'lodash';
import React, {
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import { ConnectDragSource, ConnectDropTarget } from 'react-dnd';
import { useIntl } from 'react-intl';
import { Table as ReactstrapTable } from 'reactstrap';
import {
  DRAG_AND_DROP_ICON,
  DraggableItemTypes,
  useDragAndDrop,
} from 'utils/util/util';

// Skeleton table for lists of objects: the caller renders every cell,
// including any actions column, and the table only adds the row numbering
// and optional drag-and-drop reordering. For rows edited inline, cell by
// cell, use TableEditor.

// Returns a copy of rows with the item at fromIndex moved to toIndex, or the
// same array when the move falls outside the list.
export const moveRow = <Row,>(
  rows: Row[],
  fromIndex: number,
  toIndex: number
): Row[] => {
  if (
    fromIndex < 0 ||
    fromIndex >= rows.length ||
    toIndex < 0 ||
    toIndex >= rows.length ||
    fromIndex === toIndex
  ) {
    return rows;
  }
  return update(rows, {
    $splice: [
      [fromIndex, 1],
      [toIndex, 0, rows[fromIndex]],
    ],
  });
};

export interface TableColumn<Row> {
  key: string;
  header?: ReactNode;
  render: (row: Row, index: number) => ReactNode;
  width?: string;
  className?: string;
}

export interface TableProps<Row> {
  name: string;
  rows: Row[];
  columns: TableColumn<Row>[];
  rowKey?: (row: Row, index: number) => string | number;
  // Enables drag-and-drop reordering
  onChange?: (rows: Row[]) => void;
  disabled?: boolean;
  showRowNumbers?: boolean;
  rowClassName?: (row: Row, index: number) => string;
  emptyState?: ReactNode;
  className?: string;
}

interface RowProps<Row> {
  name: string;
  row: Row;
  index: number;
  columns: TableColumn<Row>[];
  reorderable: boolean;
  showRowNumbers: boolean;
  className?: string;
  moveRow: (fromIndex: number, toIndex: number) => void;
}

const TableRowComponent = <Row,>({
  name,
  row,
  index,
  columns,
  reorderable,
  showRowNumbers,
  className,
  moveRow,
}: RowProps<Row>) => {
  const { formatMessage } = useIntl();
  const dragId = useRef(lodashUniqueId('table-row-'));
  // the shared hook is untyped and returns a heterogeneous tuple
  const [dragAndDropRef, handlerId, drop, isDragging, drag] = useDragAndDrop(
    DraggableItemTypes.TABLE_ROW,
    dragId.current,
    index,
    moveRow,
    reorderable
  ) as unknown as [
    React.RefObject<HTMLTableRowElement>,
    string | symbol | null,
    ConnectDropTarget,
    boolean,
    ConnectDragSource,
  ];

  useEffect(() => {
    drag(drop(dragAndDropRef));
  }, [drag, drop, dragAndDropRef]);

  return (
    <tr
      ref={dragAndDropRef}
      data-handler-id={handlerId}
      className={className}
      style={{ opacity: isDragging ? 0.4 : 1 }}
      data-testid={`${name}-row-${index}`}
    >
      {showRowNumbers && (
        <th scope="row" className="position-relative">
          {reorderable && (
            <span
              role="button"
              className="d-inline-block hover-child position-absolute text-muted"
              style={{ left: '-1.35rem' }}
              aria-label={formatMessage({
                id: 'app.views.widgets.inputs.table.drag_handle',
                defaultMessage: 'Drag to reorder',
              })}
            >
              {DRAG_AND_DROP_ICON}
            </span>
          )}
          {index + 1}
        </th>
      )}
      {columns.map((column) => (
        <td key={column.key} className={column.className}>
          {column.render(row, index)}
        </td>
      ))}
    </tr>
  );
};

const TableRow = React.memo(TableRowComponent) as typeof TableRowComponent;

const Table = <Row,>({
  name,
  rows,
  columns,
  rowKey,
  onChange,
  disabled = false,
  showRowNumbers = true,
  rowClassName,
  emptyState,
  className = 'table-sm align-middle mb-3',
}: TableProps<Row>) => {
  const reorderable = !!onChange && !disabled;

  const onMoveRow = useCallback(
    (fromIndex: number, toIndex: number) => {
      const next = moveRow(rows, fromIndex, toIndex);
      if (onChange && next !== rows) {
        onChange(next);
      }
    },
    [onChange, rows]
  );

  const header = useMemo(
    () => (
      <thead>
        <tr>
          {showRowNumbers && <th style={{ width: '2rem' }}>#</th>}
          {columns.map((column) => (
            <th
              key={column.key}
              style={column.width ? { width: column.width } : undefined}
            >
              {column.header}
            </th>
          ))}
        </tr>
      </thead>
    ),
    [columns, showRowNumbers]
  );

  if (rows.length === 0) {
    return <div data-testid={name}>{emptyState}</div>;
  }

  return (
    <div data-testid={name}>
      <ReactstrapTable className={className} responsive>
        {header}
        <tbody>
          {rows.map((row, index) => (
            <TableRow
              key={rowKey ? rowKey(row, index) : `${name}-${index}`}
              name={name}
              row={row}
              index={index}
              columns={columns}
              reorderable={reorderable}
              showRowNumbers={showRowNumbers}
              className={rowClassName?.(row, index)}
              moveRow={onMoveRow}
            />
          ))}
        </tbody>
      </ReactstrapTable>
    </div>
  );
};

export default Table;
